import { Maybe } from '@/types/globals';
import { Injectable } from '@angular/core';
import { ProcedureEnum } from '@core/procedure-helpers/models/procedure.enum';
import { SpecificationEnum } from '@core/procedure-helpers/models/specification.enum';
import { LoggerService } from '@core/services/logger/logger.service';
import { DentureDetailsQuery } from '@modules/denture-details/state/denture-details.query';
import { CaseTypeFormStateService } from '@modules/order/services/case-type-form-state.service';
import { OrderQuery } from '@modules/order/state/order-query';
import { PatientQuery } from '@modules/patient/state/patient-query';
import { Tooth } from '@modules/teeth-diagram/models/tooth';
import { UnitTypesInBridge } from '@modules/teeth-diagram/models/unit-type-in-bridge.enum';
import { UnitTypes } from '@modules/teeth-diagram/models/unit-type.enum';
import { positiveIntegersOnlyRegExp } from '@shared/models/consts';
import { Doctor } from '@shared/models/doctor';
import { PopupIconNames } from '@shared/models/enums/popup-icon.enum';
import { PopUpActions } from '@shared/models/enums/popup-modal-actions.enum';
import { IdName } from '@shared/models/id-name';
import { MaterialOption } from '@shared/models/material-option';
import { ProcedureMap } from '@shared/models/procedure-map';
import { OrderInformationModel } from '@shared/models/rx-models/interfaces/order-information-model';
import { PatientModelDto } from '@shared/models/rx-models/interfaces/patient-model-dto';
import { RxModel } from '@shared/models/rx-models/interfaces/rx-model';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { Observable } from 'rxjs';
import { BridgeService } from './bridge.service';
import { PopupService } from './popup.service';
import { RxMaterialRulesService } from './rx-rules-helper/rx-material-rules.service';

interface ValidationPair {
	isMandatory: (t: Tooth) => boolean;
	isInvalid: (t: Tooth) => boolean;
}

@Injectable({ providedIn: 'root' })
export class ValidateRxService {
	private name = 'ValidateRxService';
	private static get requiredTypeProcedureIds() {
		return [ProcedureEnum.Appliance, ProcedureEnum.ImplantPlanning, ProcedureEnum.Denture_Removable, ProcedureEnum.Invisalign];
	}

	constructor(
		private popupService: PopupService,
		private bridgeService: BridgeService,
		private rxMaterialRulesService: RxMaterialRulesService,
		private caseTypeFormStateService: CaseTypeFormStateService,
		private patientQuery: PatientQuery,
		private shellQuery: ShellQuery,
		private orderQuery: OrderQuery,
		private logger: LoggerService
	) {}

	static isProcedureFlowOrderValid(procedureMap: Maybe<ProcedureMap>): boolean {
		return (
			!!procedureMap?.Id &&
			(!ValidateRxService.requiredTypeProcedureIds.includes(procedureMap.ProcedureId) || procedureMap.TypeId > 0)
		);
	}

	validateRxForSave({
		patient,
		caseType,
		currentAlignerId,
		procedureMap,
		doctor,
		rx
	}: {
		patient: Maybe<PatientModelDto>;
		caseType: Maybe<IdName>;
		currentAlignerId: Maybe<string>;
		procedureMap: Maybe<ProcedureMap>;
		doctor: Maybe<Doctor>;
		rx: Maybe<RxModel>;
	}): boolean {
		const isCaseTypeValid = this.isCaseTypeValid(caseType);
		const isCurrentAlignerIdValid = this.isCurrentAlignerIdValid(caseType, currentAlignerId);
		const isDoctorValid = this.isDoctorValid(doctor);
		const isProcedureFlowOrderValid = ValidateRxService.isProcedureFlowOrderValid(procedureMap);
		const isPatientValid = this.isPatientValid(patient || rx?.Patient);
		const isRxAttachmentsReady = this.shellQuery.isRxAttachmentsReady;

		const isOrderValid = (isCaseTypeValid && isCurrentAlignerIdValid) || isProcedureFlowOrderValid;

		return isOrderValid && isDoctorValid && isPatientValid && isRxAttachmentsReady;
	}

	isPatientValid(patient: Maybe<PatientModelDto>): boolean {
		const isPatientInConflict = this.patientQuery.isPatientInConflict;

		return !!patient?.FirstName && !!patient?.LastName && !isPatientInConflict;
	}

	isCaseTypeValid(caseType: Maybe<IdName>): boolean {
		return !!caseType?.Id;
	}

	isDoctorValid(doctor: Maybe<Doctor>): boolean {
		return !!doctor?.Licence && !!doctor?.Id;
	}

	openValidationPopup(): Observable<PopUpActions> {
		const popUpInput = {
			titleTranslationKey: 'Popup.Confirmation',
			contentTranslationKey: 'Validation.InvalidRxMessage',
			iconName: PopupIconNames.Question
		};

		return this.popupService.openConfirmationPopUp({ popUpInput });
	}

	isOrderInfoInvalidForSend(orderInfo: Maybe<OrderInformationModel>): boolean {
		const isOrderInfoInvalid =
			!orderInfo ||
			((orderInfo.LocalIdeCadCamSystemId === null ||
				orderInfo.LocalIdeCadCamSystemId === undefined ||
				orderInfo.LocalIdeCadCamSystemId <= 0) &&
				(orderInfo.NumOfModels === null || orderInfo.NumOfModels === undefined || orderInfo.NumOfModels <= 0));

		if (isOrderInfoInvalid) {
			this.logger.info('Rx has invalid order information for send.', {
				module: this.name
			});
		}

		return isOrderInfoInvalid;
	}

	isTreatmentInvalidForSend(tooth: Tooth): boolean {
		if (this.shellQuery.isProcedureFlow) {
			return this.isTreatmentInvalidForSendV1(tooth);
		}

		return this.isTreatmentInvalidForSendV0(tooth);
	}

	isTreatmentInvalidForSendV1(tooth: Tooth): boolean {
		const config = tooth.BridgeIndex ? this.getTreatmentValidationInBridgeConfig() : this.getTreatmentValidationConfig();

		for (const { isMandatory, isInvalid } of Object.values(config)) {
			if (isMandatory(tooth) && isInvalid(tooth)) {
				this.logger.info(`Tooth ${tooth.ToothID} is mandatory but invalid`, {
					module: this.name
				});

				return true;
			}
		}

		return false;
	}

	isDentureDetailsInvalidForSend(dentureDetails: DentureDetailsQuery): boolean {
		const isDentureDetailsInfoInvalid = !dentureDetails.shadeSystem || !dentureDetails.teethShade || !dentureDetails.gingival;

		if (isDentureDetailsInfoInvalid) {
			this.logger.info('Rx has invalid order information for send.', {
				module: this.name
			});
		}

		return isDentureDetailsInfoInvalid;
	}

	isDentureDetailsValidForSend(dentureDetails: DentureDetailsQuery): boolean {
		return !this.isDentureDetailsInvalidForSend(dentureDetails);
	}

	isTreatmentInvalidForSendV0(tooth: Tooth): boolean {
		const isBridgeTooth = !tooth.UnitTypeID;
		const unitTypeWithoutValidation =
			tooth.UnitTypeID &&
			[UnitTypes.Detachable, UnitTypes.Missing_edentulousSpace, UnitTypes.Missing_noSpace].includes(tooth.UnitTypeID);

		if (
			(isBridgeTooth && this.bridgeService.isPonticMDAbutmentOrMissing(tooth.ToothInBridgeTypeID)) ||
			(!isBridgeTooth && unitTypeWithoutValidation)
		) {
			return false;
		}

		if (!this.orderQuery.isGlidewellOrder && (!tooth.MaterialID || tooth.MaterialID === -1)) {
			this.logger.info(`Invalid material for the tooth ${tooth.ToothID}`, {
				module: this.name
			});

			return true;
		}

		const unitTypeId = isBridgeTooth
			? this.bridgeService.convertFromBridgeToothUnitType({ unitTypeInBridge: tooth.ToothInBridgeTypeID })
			: tooth.UnitTypeID;

		const materialOption = this.orderQuery.isGlidewellOrder
			? this.getUniversalGlidewellMaterial()
			: // @ts-expect-error - TODO: remove this and fix type error after updating Tooth model
				this.rxMaterialRulesService.getMaterialUnitTypeOption({ materialId: tooth.MaterialID, unitTypeId });

		const isInvalidShadeBody = !materialOption && materialOption!.BodyShadeMandatory && !tooth.ShadeBody;
		const isInvalidMarginDesign =
			!materialOption && materialOption!.MarginDesignMandatory && (!tooth.MarginDesignLingualId || !tooth.MarginDesignBuccalId);
		const isInvalidPrepDesign =
			!materialOption &&
			materialOption!.PrepDesignMandatory &&
			(!tooth.PreparationDesignBuccalId || !tooth.PreparationDesignLingualId);
		const isInvalidStumpShade = !materialOption && materialOption!.StumpShadeMandatory && !tooth.StumpfShade;
		const isInvalidImplantType = unitTypeId === UnitTypes.ScanBody && this.isImplantsInvalid(tooth);

		const isInvalid =
			!materialOption ||
			isInvalidShadeBody ||
			isInvalidMarginDesign ||
			isInvalidPrepDesign ||
			isInvalidStumpShade ||
			isInvalidImplantType;

		if (isInvalid) {
			this.logger.info(
				`Invalid treatment for the tooth ${tooth.ToothID}. ` +
					`isInvalidShadeBody: ${isInvalidShadeBody} ` +
					`isInvalidMarginDesign: ${isInvalidMarginDesign} ` +
					`isInvalidPrepDesign: ${isInvalidPrepDesign} ` +
					`isInvalidStumpShade: ${isInvalidStumpShade} ` +
					`isInvalidImplantType: ${isInvalidImplantType}`,
				{
					module: this.name
				}
			);
		}

		return isInvalid;
	}

	private isCrownSectionInvalidV1(tooth: Tooth): boolean {
		if (!tooth.MaterialID || tooth.MaterialID === -1) {
			return true;
		}

		// material group is needed for custom materials when they weren't loaded
		const materialOption = this.rxMaterialRulesService.getMaterialSpecOption(
			tooth.MaterialID,
			tooth.SpecificationId ?? SpecificationEnum.None,
			tooth.CustomLabMaterialGroup
		);

		if (!materialOption) {
			return true;
		}

		return (
			(!!materialOption.BodyShadeMandatory && !tooth.ShadeBody) ||
			(!!materialOption.MarginDesignMandatory && (!tooth.MarginDesignLingualId || !tooth.MarginDesignBuccalId)) ||
			(!!materialOption.PrepDesignMandatory && (!tooth.PreparationDesignBuccalId || !tooth.PreparationDesignLingualId)) ||
			(!!materialOption.StumpShadeMandatory && !tooth.StumpfShade)
		);
	}

	private getTreatmentValidationConfig(): Record<string, ValidationPair> {
		const isMandatory = (t: Tooth) => ![UnitTypes.Detachable, UnitTypes.SupportingTooth, UnitTypes.Missing].includes(t.UnitTypeID ?? 0);
		const isImplantPosition = (t: Tooth) => t.UnitTypeID === UnitTypes.ImplantPosition;

		return {
			forCrown: {
				isMandatory: (t: Tooth) => isMandatory(t) && !isImplantPosition(t) && this.isCrownMandatory(),
				isInvalid: (t: Tooth) => this.isCrownSectionInvalidV1(t)
			},
			forScanBody: {
				isMandatory: (t: Tooth) => isMandatory(t) && UnitTypes.ImplantBased === t.UnitTypeID && !this.orderQuery.isGlidewellOrder,
				isInvalid: (t: Tooth) => this.isImplantsInvalid(t)
			}
		};
	}

	private getTreatmentValidationInBridgeConfig(): Record<string, ValidationPair> {
		const isMandatory = (t: Tooth) => ![UnitTypesInBridge.Missing].includes(t.ToothInBridgeTypeID ?? 0);

		return {
			forCrown: {
				isMandatory: (t: Tooth) => isMandatory(t) && this.isCrownMandatory(),
				isInvalid: (t: Tooth) => this.isCrownSectionInvalidV1(t)
			},
			forScanBody: {
				isMandatory: (t: Tooth) => isMandatory(t) && t.ToothInBridgeTypeID === UnitTypesInBridge.ImplantBased,
				isInvalid: (t: Tooth) => this.isImplantsInvalid(t)
			}
		};
	}

	private isCrownMandatory(): boolean {
		return !(this.orderQuery.isGlidewellOrderV1 || this.orderQuery.isDentureOrderV1);
	}

	private isCurrentAlignerIdValid(caseType: Maybe<IdName>, currentAlignerId: Maybe<string>): boolean {
		if (!this.caseTypeFormStateService.isCurrentAlignerRequired({ caseType })) {
			return true;
		}

		return !currentAlignerId || (!!positiveIntegersOnlyRegExp.exec(currentAlignerId) && +currentAlignerId <= 99);
	}

	private getUniversalGlidewellMaterial(): MaterialOption {
		return {
			StumpShadeMandatory: false,
			BodyShadeMandatory: false,
			MarginDesignMandatory: false,
			PrepDesignMandatory: false
		};
	}

	private isImplantsInvalid(tooth: Tooth): boolean {
		if (tooth.ImplantTypeID && !tooth.ImplantsViewData) {
			return false;
		}

		if (!tooth.ImplantTypeID && tooth.ImplantsViewData && !this.isImplantsViewDataInvalid(tooth)) {
			return false;
		}

		return true;
	}

	private isImplantsViewDataInvalid(tooth: Tooth): boolean {
		return (
			!tooth.ImplantsViewData?.ImplantManufacturerId ||
			!tooth.ImplantsViewData?.ImplantConnectionId ||
			!tooth.ImplantsViewData?.ImplantPlatformId ||
			!tooth.ImplantsViewData?.ScanBodyId
		);
	}
}
